import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
    getNotificationInbox,
    markNotificationAsRead,
} from '@/services/notification/inbox/notificationInboxApi';

import type { NotificationInboxItem } from '@/services/notification/inbox/types';

interface NotificationInboxState {
    urgentAlertLst: NotificationInboxItem[];
    announcementLst: NotificationInboxItem[];
    unreadCnt: number;
    loading: boolean;
    loaded: boolean;
    error: string | null;
}

const initialState: NotificationInboxState = {
    urgentAlertLst: [],
    announcementLst: [],
    unreadCnt: 0,
    loading: false,
    loaded: false,
    error: null,
};

export const fetchNotificationInbox = createAsyncThunk(
    'notificationInbox/fetch',
    async (_arg: { forceRefresh?: boolean } | void, { rejectWithValue }) => {
        try {
            const response = await getNotificationInbox();
            return response.data;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to load notifications';
            return rejectWithValue(message);
        }
    },
    {
        condition: (arg, { getState }) => {
            const state = getState() as { notificationInbox: NotificationInboxState };
            const { loading, loaded } = state.notificationInbox;
            const forceRefresh = Boolean(arg && typeof arg === 'object' && arg.forceRefresh);
            if (loading) {
                return false;
            }
            if (loaded && !forceRefresh) {
                return false;
            }
            return true;
        },
    },
);

export const markInboxNotificationRead = createAsyncThunk(
    'notificationInbox/markRead',
    async (usrNtfctnInboxId: string, { rejectWithValue }) => {
        try {
            const response = await markNotificationAsRead(usrNtfctnInboxId);
            return response.data;
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Failed to mark notification as read';
            return rejectWithValue(message);
        }
    },
);

const markItemRead = (item: NotificationInboxItem, inboxId: string): NotificationInboxItem =>
    item.usrNtfctnInboxId === inboxId ? { ...item, isRd: 'Y' } : item;

const notificationInboxSlice = createSlice({
    name: 'notificationInbox',
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotificationInbox.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotificationInbox.fulfilled, (state, action) => {
                state.loading = false;
                state.loaded = true;
                state.urgentAlertLst = action.payload.urgentAlertLst ?? [];
                state.announcementLst = action.payload.announcementLst ?? [];
                state.unreadCnt = action.payload.unreadCnt ?? 0;
            })
            .addCase(fetchNotificationInbox.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to load notifications';
            })
            .addCase(markInboxNotificationRead.fulfilled, (state, action) => {
                const inboxId = action.payload.usrNtfctnInboxId;
                const wasUnread =
                    state.announcementLst.some(
                        (item) => item.usrNtfctnInboxId === inboxId && item.isRd === 'N',
                    ) ||
                    state.urgentAlertLst.some(
                        (item) => item.usrNtfctnInboxId === inboxId && item.isRd === 'N',
                    );

                state.announcementLst = state.announcementLst.map((item) =>
                    markItemRead(item, inboxId),
                );
                state.urgentAlertLst = state.urgentAlertLst.map((item) =>
                    markItemRead(item, inboxId),
                );

                if (wasUnread && state.unreadCnt > 0) {
                    state.unreadCnt -= 1;
                }
            });
    },
});

export const selectNotificationAnnouncementList = (state: {
    notificationInbox: NotificationInboxState;
}) => state.notificationInbox.announcementLst;
export const selectNotificationUrgentAlertList = (state: {
    notificationInbox: NotificationInboxState;
}) => state.notificationInbox.urgentAlertLst;
export const selectNotificationUnreadCount = (state: {
    notificationInbox: NotificationInboxState;
}) => state.notificationInbox.unreadCnt;
export const selectNotificationLoaded = (state: { notificationInbox: NotificationInboxState }) =>
    state.notificationInbox.loaded;
export const selectNotificationLoading = (state: { notificationInbox: NotificationInboxState }) =>
    state.notificationInbox.loading;

export default notificationInboxSlice.reducer;
